'use client';

import { useCallback, useMemo, useState } from 'react';
import { McpCredentialFields, toasterApi } from '@terra/ui-design-system';
import type {
  UpsertMcpCredentialDto,
  McpServerResponseDto,
} from '@terra/shared-contracts-mcp-servers';
import type { McpFormCredentialType } from '@terra/shared-contracts-mcp-servers';
import { resolveMcpOAuthScopeDiscovery } from '@terra/shared-rest-clients-mcp-servers';
import { useTranslations } from 'next-intl';
import {
  useAuthorizeTenantMcpServer,
  useTenantMcpServer,
  useUpsertTenantMcpCredential,
  useCompleteMcpOAuthCallback,
} from '../../../hooks/api/useMcpServers';
import { authorizeMcpOAuthWithElectron } from './electronMcpOAuth';

type AiApplicationMcpCredentialFieldsProps = {
  mcpServerId?: string | null;
  existingServer?: McpServerResponseDto | null;
  discoveryUrl: string;
  onCredentialChange: (credential: UpsertMcpCredentialDto | null) => void;
  onCredentialTypeChange?: (credentialType: McpFormCredentialType) => void;
  resolveMcpServerId?: () => Promise<string | null>;
  isReadOnly?: boolean;
  enableOAuthAuthorizeButton?: boolean;
  showNoneConfirmButton?: boolean;
  credentialTypeSeed?: string | null;
  credentialIsEditing?: boolean;
  hideAuthTypeSelector?: boolean;
  oauthIdentityId?: string | null;
  initialAvailableOAuthScopes?: string[];
  initialOauthScopes?: string;
};

export const AiApplicationMcpCredentialFields = ({
  mcpServerId,
  existingServer,
  discoveryUrl,
  onCredentialChange,
  onCredentialTypeChange,
  resolveMcpServerId,
  isReadOnly,
  enableOAuthAuthorizeButton = true,
  showNoneConfirmButton = true,
  credentialTypeSeed,
  credentialIsEditing,
  hideAuthTypeSelector,
  oauthIdentityId,
  initialAvailableOAuthScopes,
  initialOauthScopes,
}: AiApplicationMcpCredentialFieldsProps) => {
  const t = useTranslations('settings.mcpServerForm');
  const { server: fetchedServer } = useTenantMcpServer({
    id: mcpServerId ?? '',
  });
  const resolvedServer = existingServer ?? fetchedServer;

  const [draftCredential, setDraftCredential] =
    useState<UpsertMcpCredentialDto | null>(null);
  const [isAuthorizing, setIsAuthorizing] = useState(false);

  const authorizeMutation = useAuthorizeTenantMcpServer();
  const completeOAuthCallbackMutation = useCompleteMcpOAuthCallback();
  const upsertCredentialMutation = useUpsertTenantMcpCredential();

  const handleDiscoverOAuthScopes = useCallback(
    (nextDiscoveryUrl: string) =>
      resolveMcpOAuthScopeDiscovery({
        discoveryUrl: nextDiscoveryUrl,
        mcpServerId,
        oauthIdentityId,
        persistedDiscoveryUrl: resolvedServer?.url ?? null,
      }),
    [mcpServerId, oauthIdentityId, resolvedServer?.url],
  );

  const oauthMessages = useMemo(
    () => ({
      oauthPopupBlocked: t('oauthPopupBlocked'),
      oauthAuthorizationFailed: t('oauthAuthorizationFailed'),
      oauthAuthorizationTimedOut: t('oauthAuthorizationTimedOut'),
      oauthAuthorizationWindowClosed: t('oauthAuthorizationWindowClosed'),
      oauthAuthorizationIncomplete: t('oauthAuthorizationIncomplete'),
    }),
    [t],
  );

  const handleCredentialChange = useCallback(
    (credential: UpsertMcpCredentialDto | null) => {
      setDraftCredential(credential);
      onCredentialChange(credential);
    },
    [onCredentialChange],
  );

  const handleAuthorizeOAuth = useCallback(async () => {
    let resolvedMcpServerId = mcpServerId ?? null;
    if (!resolvedMcpServerId && resolveMcpServerId) {
      resolvedMcpServerId = await resolveMcpServerId();
    }
    if (!resolvedMcpServerId) {
      toasterApi.error({
        title: t('oauthAuthorizationFailed'),
        description: t('validationCredentialsRequired'),
      });
      return;
    }

    setIsAuthorizing(true);
    try {
      if (draftCredential) {
        await upsertCredentialMutation.mutateAsync({
          id: resolvedMcpServerId,
          credential: draftCredential,
        });
      }

      await authorizeMcpOAuthWithElectron({
        fetchAuthorizeUrl: () =>
          authorizeMutation.mutateAsync(resolvedMcpServerId),
        completeOAuthCallback: async ({ code, state }) => {
          await completeOAuthCallbackMutation.mutateAsync({ code, state });
        },
        messages: oauthMessages,
      });
    } catch (error: unknown) {
      toasterApi.error({
        title: t('oauthAuthorizationFailed'),
        description:
          error instanceof Error
            ? error.message
            : t('oauthAuthorizationFailed'),
      });
    } finally {
      setIsAuthorizing(false);
    }
  }, [
    authorizeMutation,
    completeOAuthCallbackMutation,
    draftCredential,
    mcpServerId,
    oauthMessages,
    resolveMcpServerId,
    t,
    upsertCredentialMutation,
  ]);

  if (isReadOnly) {
    return null;
  }

  const showAuthorizeAction =
    enableOAuthAuthorizeButton && Boolean(mcpServerId ?? resolveMcpServerId);

  const resolvedInitialCredentialType =
    credentialTypeSeed !== undefined
      ? credentialTypeSeed
      : (resolvedServer?.credentialType ?? null);

  const resolvedCredentialIsEditing =
    credentialIsEditing ?? Boolean(mcpServerId ?? resolvedServer?.id);

  return (
    <McpCredentialFields
      discoveryUrl={discoveryUrl}
      isEditing={resolvedCredentialIsEditing}
      initialCredentialType={resolvedInitialCredentialType}
      initialAvailableOAuthScopes={initialAvailableOAuthScopes}
      initialOauthScopes={initialOauthScopes}
      onCredentialChange={handleCredentialChange}
      onCredentialTypeChange={onCredentialTypeChange}
      showNoneConfirmButton={showNoneConfirmButton}
      hideAuthTypeSelector={hideAuthTypeSelector}
      discoverOAuthScopes={handleDiscoverOAuthScopes}
      authorizeAction={
        showAuthorizeAction
          ? {
              onClick: () => {
                void handleAuthorizeOAuth();
              },
              disabled: isAuthorizing,
            }
          : undefined
      }
    />
  );
};
